import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { InvestmentsAPI, InvestmentPayload } from '../services/investments.ts';
import {
  formatCurrencyInput,
  applyCurrencyMask,
  parseCurrency,
  getCurrencyLocale,
} from '../utils/currency.ts';
import FormPage from '../components/ui/FormPage.tsx';
import MoreOptions from '../components/ui/MoreOptions.tsx';

const EditInvestment: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<InvestmentPayload>({
    description: '',
    category: 'fixed_income',
    investedValue: 0,
    currentValue: undefined,
    status: 'active',
    currency: 'BRL',
    ticker: '',
    quantity: null,
  });
  const [investedValueDisplay, setInvestedValueDisplay] = useState('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [originalCurrent, setOriginalCurrent] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const data = await InvestmentsAPI.get(id);
        setForm({
          description: data.description,
          category: data.category,
          investedValue: data.investedValue,
          currentValue: data.currentValue,
          status: data.status,
          currency: data.currency || 'BRL',
          ticker: data.ticker || '',
          quantity: data.quantity ?? null,
          unitPrice: data.unitPrice ?? null,
        });
        setOriginalCurrent(Number(data.currentValue));
        setInvestedValueDisplay(formatCurrencyInput(Number(data.investedValue || 0)));
        setCurrentValueDisplay(
          data.currentValue != null ? formatCurrencyInput(Number(data.currentValue)) : ''
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const updateMutation = useMutation(async () => InvestmentsAPI.update(String(id), form), {
    onSuccess: () => {
      queryClient.invalidateQueries('investments');
      queryClient.invalidateQueries(['investment', id]);
      queryClient.invalidateQueries(['investment-price-history', id]);
      queryClient.invalidateQueries(['investments-portfolio']);
      navigate(`/investments/${id}`);
    },
    onError: () => setErrors(prev => ({ ...prev, submit: 'Erro ao salvar alterações.' })),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }) as any);
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyChange = (field: 'investedValue' | 'currentValue', rawValue: string) => {
    const locale = getCurrencyLocale(form.currency || 'BRL');
    const masked = applyCurrencyMask(rawValue, locale);
    const numeric = parseCurrency(masked, locale);
    if (field === 'investedValue') setInvestedValueDisplay(masked);
    else setCurrentValueDisplay(masked);
    setForm(prev => ({ ...prev, [field]: numeric }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!form.description?.trim()) nextErrors.description = 'Nome / descrição é obrigatório';
    if (!form.category) nextErrors.category = 'Categoria é obrigatória';
    if (!form.status) nextErrors.status = 'Status é obrigatório';
    if (!form.investedValue || form.investedValue <= 0) {
      nextErrors.investedValue = 'Valor investido deve ser maior que zero';
    }
    if (form.currentValue !== undefined && form.currentValue < 0) {
      nextErrors.currentValue = 'Valor de mercado não pode ser negativo';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  const marketChanged =
    form.currentValue != null &&
    originalCurrent != null &&
    Math.abs(Number(form.currentValue) - originalCurrent) > 0.0001;

  return (
    <FormPage
      backTo={`/investments/${id}`}
      backLabel="Voltar ao detalhe"
      breadcrumb="Patrimônio / Investimentos / Editar"
      title="Editar investimento"
      subtitle="Alterações de valor de mercado acrescentam o histórico de preços"
      hint={
        marketChanged ? (
          <>
            O valor de mercado mudou — ao salvar, um <strong>novo ponto</strong> será acrescentado
            ao histórico de preços (registros anteriores permanecem).
          </>
        ) : (
          <>
            Atualize os dados do ativo. Preferir <strong>Atualizar preço</strong> só para marcação a
            mercado.
          </>
        )
      }
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(`/investments/${id}`)}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="edit-investment-form"
            className="btn btn-primary"
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </>
      }
    >
      <form
        id="edit-investment-form"
        noValidate
        onSubmit={e => {
          e.preventDefault();
          if (validate()) updateMutation.mutate();
        }}
      >
        {errors.submit && (
          <div className="field-error" style={{ marginBottom: 12 }}>
            {errors.submit}
          </div>
        )}
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="description">Nome / descrição *</label>
            <input
              id="description"
              name="description"
              className={`input${errors.description ? ' error' : ''}`}
              value={form.description}
              onChange={handleChange}
            />
            {errors.description && <p className="field-error">{errors.description}</p>}
          </div>
          <div className="field">
            <label htmlFor="category">Categoria *</label>
            <select
              id="category"
              name="category"
              className="select"
              value={form.category}
              onChange={handleChange}
            >
              <option value="fixed_income">Renda fixa</option>
              <option value="stocks">Renda variável / Ações</option>
              <option value="funds">Fundos</option>
              <option value="crypto">Cripto</option>
              <option value="offshore">Offshore</option>
              <option value="other">Outros</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              className="select"
              value={form.status}
              onChange={handleChange}
            >
              <option value="active">Ativo</option>
              <option value="sold">Encerrado</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="investedValue">Custo / aplicação *</label>
            <input
              id="investedValue"
              className={`input${errors.investedValue ? ' error' : ''}`}
              value={investedValueDisplay}
              onChange={e => handleCurrencyChange('investedValue', e.target.value)}
              inputMode="decimal"
            />
            {errors.investedValue && <p className="field-error">{errors.investedValue}</p>}
          </div>
          <div className="field">
            <label htmlFor="currentValue">Valor de mercado</label>
            <input
              id="currentValue"
              className={`input${errors.currentValue ? ' error' : ''}`}
              value={currentValueDisplay}
              onChange={e => handleCurrencyChange('currentValue', e.target.value)}
              inputMode="decimal"
            />
            {errors.currentValue && <p className="field-error">{errors.currentValue}</p>}
          </div>
          <div className="field">
            <label htmlFor="currency">Moeda</label>
            <select
              id="currency"
              name="currency"
              className="select"
              value={form.currency || 'BRL'}
              onChange={handleChange}
            >
              <option value="BRL">BRL</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </div>

        <MoreOptions>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="ticker">Ticker / código</label>
              <input
                id="ticker"
                name="ticker"
                className="input"
                value={form.ticker || ''}
                onChange={handleChange}
              />
            </div>
            <div className="field">
              <label htmlFor="quantity">Quantidade</label>
              <input
                id="quantity"
                className="input"
                type="number"
                step="any"
                value={form.quantity ?? ''}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    quantity: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              />
            </div>
          </div>
        </MoreOptions>
      </form>
    </FormPage>
  );
};

export default EditInvestment;
